import { AppDOM, clear, text, create } from '@bbc/news-vj-search-lookup-helpers/DOMHelper';
import { createVocab } from '@bbc/news-vj-search-lookup-helpers/VocabHelper';
import { keyCodes, isPrintableKeyCode } from '@bbc/news-vj-search-lookup-helpers/KeyboardHelper';
import { hasFuzzyMatch, prepareForFuzzyMatch } from '@bbc/news-vj-search-lookup-helpers/FuzzyMatcher';
import PluginManager from './PluginManager/PluginManager';
import HTMLManager from './HtmlManager';

export default class SearchLookup {
    constructor(options) {
        try {
            this.id = options.id || 'news-vj-search-lookup';
            this.target = options.target;
            this.vocab = createVocab(options.vocab || {});
            const markup = new HTMLManager(this.vocab).getLookupHTML({
                id: this.id,
                button: options.button,
                inline: options.inline,
                minChars: options.minChars,
                pattern: options.pattern,
            }).replace(/>\s+</g, '><'); // Removes whitespace between tags for inline-bock spacing
            this.target.innerHTML = markup;
            this.data = this.transformData(options.data || {});
            this.app = new AppDOM(this.target);
            this.els = {
                input: this.app.select(`#${this.id}`),
                status: this.app.select('.news-vj-search-lookup__screen-reader-announcement'),
                button: this.app.select('.news-vj-search-lookup__button'),
                menu: this.app.select('.news-vj-search-lookup__menu'),
                wrapper: this.app.select('.news-vj-search-lookup__wrapper'),
            };
            this.hasButton = !!this.els.button;
            this.menuClassList = this.els.menu.getAttribute('class');
            this.minChars = options.minChars || 2;
            this.pattern = options.pattern || '';
            this.displayKey = options.displayKey || 'name';
            this.confirmOnBlur = options.confirmOnBlur || false;
            this.useSuggestions = typeof options.useSuggestions !== 'undefined' ? options.useSuggestions : true;
            this.errorMessage = this.vocab.noResults;
            this.pluginManager = new PluginManager({
                plugins: options.plugins,
                dataset: this.data,
            });

            this.state = {
                focusIndex: -1,
                suggestions: [],
                query: '',
                menuShouldOpen: false,
                shouldDisplayError: false,
                suppressError: false,
            };

            this.callback = options.onSelect;
            this.onLoad = (options.onLoad && options.onLoad.bind(this)) || (() => {});
            this.noResultsCallback = options.noResultsCallback || undefined;
            this.clearInput = !(options.clearInput === false);
            this.overrideResults = options.overrideResults || ((query, suggestions) => suggestions);
            this.onSearchComplete = options.onSearchComplete || ((query, suggestions) => {});

            this.handleKeyDown = this.handleKeyDown.bind(this);
            this.handleInputChange = this.handleInputChange.bind(this);
            this.handleComponentBlur = this.handleComponentBlur.bind(this);
            this.handleButtonClick = this.handleButtonClick.bind(this);
            this.sortSuggestions = this.sortSuggestions.bind(this);
            this.updateSuggestions = this.updateSuggestions.bind(this);

            this.addListeners();
            this.render();

            window.setTimeout(this.onLoad, 0);
        }
        catch (error) {
            console.error(error);
        }
    }

    transformData(dataObj) {
        const keys = Object.keys(dataObj);
        return keys.map(key => ({
            id: key,
            data: dataObj[key],
        }));
    }

    addListeners() {
        this.els.wrapper.addEventListener('keydown', this.handleKeyDown);
        this.els.input.addEventListener('input', this.handleInputChange); // @TODO: IE9 doesn't fire this event on delete/backspace/cut
        this.els.input.addEventListener('blur', this.handleComponentBlur);

        if (this.hasButton) {
            this.els.button.addEventListener('click', this.handleButtonClick);
        }
    }

    handleKeyDown(e) {
        switch (keyCodes[e.keyCode]) {
        case 'up':
            this.handleUpArrow(e);
            break;
        case 'down':
            this.handleDownArrow(e);
            break;
        case 'enter':
            this.handleEnter(e);
            break;
        case 'escape':
            this.handleComponentBlur();
            break;
        default:
            if (isPrintableKeyCode(e.keyCode)) {
                this.els.input.focus();
                this.state.focusIndex = -1;
                this.state.query = this.els.input.value;
            }
            break;
        }
    }

    handleComponentBlur() {
        const focusOnOption = this.state.focusIndex >= 0;
        const onlyOneSuggestion = this.state.suggestions.length === 1;
        const hasExactMatch = this.state.indexOfMatchingElement >= 0;
        const hasSuggestions = this.state.suggestions.length > 0;

        const canSubmitQuery = focusOnOption || onlyOneSuggestion || hasExactMatch;
        const shouldSubmitQuery = this.confirmOnBlur && hasSuggestions && canSubmitQuery;

        if (shouldSubmitQuery) {
            this.attemptSubmit();
        }
        else {
            this.state.menuShouldOpen = false;
            this.render();
        }
    }

    handleButtonClick(e) {
        e.preventDefault();
        this.attemptSubmit();
    }

    handleDownArrow(e) {
        e.preventDefault();
        const isNotAtBottom = this.state.focusIndex !== this.state.suggestions.length - 1;
        const allowMoveDown = isNotAtBottom && this.state.menuShouldOpen;
        if (allowMoveDown) {
            this.handleOptionFocus(this.state.focusIndex + 1);
        }
    }

    handleUpArrow(e) {
        e.preventDefault();
        const isNotAtTop = this.state.focusIndex !== -1;
        const allowMoveUp = isNotAtTop && this.state.menuShouldOpen;
        if (allowMoveUp) {
            this.handleOptionFocus(this.state.focusIndex - 1);
        }
    }

    handleOptionFocus(index) {
        this.state.focusIndex = index;
        this.render();
    }

    handleOptionClick(e, index) {
        this.selectOption(index);
    }

    handleEnter(e) {
        if (this.state.menuShouldOpen) {
            e.preventDefault();
            this.attemptSubmit();
        }
    }

    attemptSubmit() {
        const optionHasFocus = this.state.focusIndex >= 0;
        const onlyOneSuggestion = this.state.suggestions.length === 1;
        const hasExactMatch = this.state.indexOfMatchingElement >= 0;

        if (optionHasFocus) {
            this.selectOption(this.state.focusIndex);
        }

        if (onlyOneSuggestion) {
            this.selectOption(0);
        }

        if (hasExactMatch) {
            this.selectOption(this.state.indexOfMatchingElement);
        }
    }

    handleInputChange(e) {
        const query = e.target.value;
        const queryEmpty = query.length === 0;
        const queryChanged = this.state.query.length !== query.length;
        const queryLongEnough = query.length >= this.minChars;
        this.state.menuShouldOpen = queryLongEnough;
        this.state.query = query;

        if (!queryEmpty && queryChanged && queryLongEnough) {
            if (this.hasButton) {
                this.els.button.classList.add('news-vj-search-lookup__button--loading');
            }

            const localResults = {
                suggestions: this.suggestionFilter(query),
            };

            const queryPlugins = new Promise((resolve, reject) => {
                return this.pluginManager.query(query, resolve, reject, localResults);
            });

            queryPlugins
                .then((response = localResults) => {
                    const responseHasSuggestions = response.suggestions &&
                        response.suggestions.length > 0;

                    if (responseHasSuggestions) {
                        this.updateSuggestions(response.suggestions);
                    }
                    else if (response.error) {
                        this.state.suppressError = false;
                        this.updateError(response.error);
                    }
                    else if (response.suppressError) {
                        this.state.suppressError = true;
                        this.updateError();
                    }
                    else {
                        this.updateSuggestions([]);
                    }
                })
                .catch(err => {
                    console.error(err);
                });
        }
        else {
            this.updateSuggestions([]);
        }
    }

    getIndexOfMatchingElement() {
        let fuzzyMatch; // optimisation: avoid defining until we know we don't have an exact match
        const exactMatch = this.state.suggestions.reduce((old, curr) => {
            return (this.state.query === curr.data[this.displayKey]) ? curr : old;
        }, false);
        if (!exactMatch) {
            fuzzyMatch = this.state.suggestions.reduce((match, curr) => {
                const fuzzyName = prepareForFuzzyMatch(curr.data[this.displayKey]);
                const fuzzyQuery = prepareForFuzzyMatch(this.state.query);
                return fuzzyName === fuzzyQuery ? curr : match;
            }, {});
        }
        return this.state.suggestions.indexOf(exactMatch || fuzzyMatch);
    }

    queryPlugin(query, results, resolve, reject) {
        if (this.plugin && results.length < 1) {
            return this.plugin.query(query, resolve, reject);
        }
        return resolve(results);
    }

    updateSuggestions(suggestions) {
        this.state.suggestions = this.overrideResults(this.state.query, suggestions);
        if (this.state.suggestions.length > 0) {
            this.state.shouldDisplayError = false;
            this.render();
        }
        else {
            this.updateError();
        }
    }

    updateError(error = this.vocab.noResults) {
        this.state.shouldDisplayError = !this.state.suppressError;
        this.state.suggestions = [];
        this.errorMessage = error;
        this.render();
    }

    dealWithLocalStorage(id) {
        const LOCAL_STORAGE_KEY = 'VJ_lookups';
        const storedLookups = localStorage.getItem(LOCAL_STORAGE_KEY);
        const currentIds = storedLookups ? JSON.parse(storedLookups) : {};
        currentIds[id] = currentIds[id] ? currentIds[id] + 1 : 1;
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currentIds));
    }

    selectOption(index) {
        const newSelection = this.state.suggestions[index];
        if (newSelection) {
            // Should prevent double submission when confirmOnBlur && button
            this.state.selectedSuggestion = this.state.suggestions[index];
            this.dealWithLocalStorage(this.state.selectedSuggestion.id);
            this.callback(this.state.selectedSuggestion.id, this.state.selectedSuggestion.data);
        }
        this.state.focusIndex = -1;
        this.state.menuShouldOpen = false;
        this.state.query = this.clearInput
            ? ''
            : this.state.selectedSuggestion.data[this.displayKey];
        this.render();
    }

    suggestionFilter(query) {
        // Compares normalised query against normalised chosen display values in dataset.
        // If negative, checks against an existing synonyms array for matching strings.
        return this.data
            .filter(
                d =>
                    hasFuzzyMatch(d.data[this.displayKey], query) ||
                    (d.data.synonyms
                        ? d.data.synonyms.filter(synonym => hasFuzzyMatch(synonym, query)).length >
                              0
                        : false),
            )
            .sort(this.sortSuggestions);
    }

    sortSuggestions(a, b) {
        const valA = a.data[this.displayKey].toLowerCase();
        const valB = b.data[this.displayKey].toLowerCase();
        const q = this.state.query.toLowerCase();

        const A = valA.indexOf(q);
        const B = valB.indexOf(q);

        if (A > B) {
            return 1;
        }

        if (A < B) {
            return -1;
        }

        if (valA > valB) {
            return 1;
        }

        if (valA < valB) {
            return -1;
        }

        return 0;
    }

    setStatus(menuStatus) {
        const { suggestions } = this.state;
        const numberOfResults = suggestions.length;
        const optionHasFocus = this.state.focusIndex >= 0;

        if (this.state.query.length < this.minChars) {
            return this.vocab.minChars(this.minChars);
        }

        if (numberOfResults < 1) {
            return this.vocab.noResults;
        }

        let resultOrResults = '';
        if (menuStatus === 'visible') {
            resultOrResults = numberOfResults === 1
                ? this.vocab.oneResultAvailable(suggestions[0].data[this.displayKey])
                : this.vocab.multipleResultsAvailable(numberOfResults);
        }

        const selectedResult = optionHasFocus
            ? this.vocab.selectedResult(suggestions[this.state.focusIndex].data[this.displayKey])
            : '';

        return `${resultOrResults} ${selectedResult}`;
    }

    addSuggestionListener(evtType, el, i) {
        el.addEventListener(evtType, e => {
            this.handleOptionClick(e, i);
        });
        return el;
    }

    createSuggestion(suggestionText, index, modifier) {
        const className = 'news-vj-search-lookup__option';
        const hasFocus = this.state.focusIndex === index;
        const li = create('li', {
            class: modifier ? `${className} ${className}--${modifier}` : className,
            id: `${this.id}__option--${index}`,
            role: 'option',
        });
        li.setAttribute('aria-selected', hasFocus);
        li.innerHTML = suggestionText;
        return li;
    }

    stripHtml(html) {
       var doc = new DOMParser().parseFromString(html, 'text/html');
       return doc.body.textContent || '';
    }

    render() {
        const {
            menuShouldOpen,
            focusIndex,
            suggestions,
            query,
            shouldDisplayError,
        } = this.state;

        this.els.wrapper.setAttribute('aria-expanded', menuShouldOpen);

        // Update Input
        const optionFocusId = `${this.id}__option--${focusIndex}`;
        const optionFocused = focusIndex !== -1;
        if (optionFocused) {
            this.els.input.setAttribute('aria-activedescendant', optionFocusId);
            this.els.input.value = suggestions[focusIndex].data[this.displayKey];
        }
        else {
            this.els.input.value = query;
            this.els.input.removeAttribute('aria-activedescendant');
        }

        // Update Button
        this.state.indexOfMatchingElement = this.getIndexOfMatchingElement();

        if (this.useSuggestions) {
            if (this.hasButton) {
                const hasOneSuggestion = suggestions.length === 1;
                const hasExactMatch = this.state.indexOfMatchingElement >= 0;
                const willSubmitOnBlur = this.confirmOnBlur && suggestions.length > 0;
                const shouldEnableButton = optionFocused ||
                    willSubmitOnBlur ||
                    hasOneSuggestion ||
                    hasExactMatch;
                if (shouldEnableButton) {
                    this.els.button.removeAttribute('disabled');
                }
                else {
                    this.els.button.setAttribute('disabled', true);
                }
                this.els.button.classList.remove('news-vj-search-lookup__button--loading');
            }

            // Update Menu Suggestions
            const menuClassModifier = menuShouldOpen ? 'visible' : 'hidden';
            this.els.menu.setAttribute(
                'class',
                `${this.menuClassList} news-vj-search-lookup__menu--${menuClassModifier}`,
            );

            clear(this.els.menu);

            // Update screen reader announcement
            clear(this.els.status);
            const statusText = text(this.stripHtml(this.setStatus(menuClassModifier)));
            this.els.status.appendChild(statusText);

            const suggestionList = suggestions
                .map(
                    (suggestion, i) =>
                        focusIndex === i
                            ? this.createSuggestion(suggestion.data[this.displayKey], i, 'focused')
                            : this.createSuggestion(suggestion.data[this.displayKey], i),
                )
                .map((suggestion, i) => this.addSuggestionListener('mousedown', suggestion, i));

            if (shouldDisplayError) {
                suggestionList.push(this.createSuggestion(this.errorMessage, 0, 'error-message'));
                if (this.noResultsCallback) suggestionList[0].addEventListener('mousedown', this.noResultsCallback);
            }

            suggestionList.forEach(suggestion => {
                this.els.menu.appendChild(suggestion);
            });
        }

        this.els.menu.scrollTop = 0;
        this.pluginManager.postRender(this.els);
    }
}
